import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { EmailChannel } from '@/types/emailChannel';
import { getAuthToken } from '@/utils/auth/tokenManager';
import api from '@/api/Api';

/**
 * State type for email channels management in Redux.
 */
export interface EmailChannelsState {
  channels: EmailChannel[];
  defaultChannel: EmailChannel | null;
  hasDomainVerified: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: EmailChannelsState = {
  channels: [],
  defaultChannel: null,
  hasDomainVerified: false,
  loading: false,
  error: null,
};

// Async thunk to fetch channels from the new API endpoint
export const fetchChannels = createAsyncThunk(
  'emailChannels/fetchChannels',
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();

      const response = await api.get('/email-channels', {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      if (!response.data || !response.data.data.emailChannels) {
        throw new Error('Invalid server response');
      }
      // Map the returned API data to our state structure
      const { emailChannels, doesUserHaveVerifiedDomain } = response.data.data;
      // Find the default channel (isDefault true)
      const defaultChannelRaw = emailChannels.find((c: any) => c.isDefault);

      // Convert api channels to EmailChannel[] format
      const channels: EmailChannel[] = emailChannels.map((c: any) => ({
        id: c.id,
        name: c.name,
        email: c.emailAddress ?? '', // Backend sends `emailAddress`
        channelName: c.name || c.channelName || '', // fallback
        status: c.isActive ? 'active' : 'inactive',
        isDefault: !!c.isDefault,
        isActive: !!c.isActive,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        type: c.type || '', // not present, set empty or add mapping
        senderName: c.senderName ?? '',
        autoBccEmail: c.autoBccMail ?? '',
        noReplyEmail: c.noReplyMail ?? '',
        agentsCanInitiateConvo: c.allowAgentOutbound ?? false,
        useAgentNamesInEmails: c.allowAgentName ?? false,
        forwardUseOriginalSender: c.orignalSenderAsRequester ?? false,
        icon: c.emoji ?? undefined,
        teamId: c.teamId ?? undefined,
        isVerified: true, // not present (assume verified or adjust if needed)
        // Add any other mapping needed here
      }));

      let defaultChannel: EmailChannel | null = null;
      if (defaultChannelRaw) {
        defaultChannel = channels.find((ch) => ch.id === defaultChannelRaw.id) || null;
      }

      return {
        channels,
        defaultChannel,
        hasDomainVerified: !!doesUserHaveVerifiedDomain,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createChannel = createAsyncThunk(
  'emailChannels/createChannel',
  async (channel: Omit<EmailChannel, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      const token = getAuthToken();

      const response = await api.post('/email-channels', channel, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      if (!response.data || !response.data.data) {
        throw new Error('Invalid server response');
      }

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateChannel = createAsyncThunk(
  'emailChannels/updateChannel',
  async ({ id, updates }: { id: string; updates: Partial<EmailChannel> }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();

      const response = await api.patch(`/email-channels/${id}`, updates, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      if (!response.data) {
        throw new Error('Failed to update channel');
      }

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteChannel = createAsyncThunk(
  'emailChannels/deleteChannel',
  async (id: string, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await api.delete(`/email-channels/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      if (!response.data || response.data.status !== 'success') {
        throw new Error('Failed to delete channel');
      }
      // Return the deleted channel id to update local state
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleChannelStatus = createAsyncThunk(
  'emailChannels/toggleChannelStatus',
  async (id: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const channel = state.emailChannels?.channels?.find(c => c.id === id);
      if (!channel) {
        throw new Error('Channel not found');
      }
      const token = getAuthToken();
      const response = await api.patch(
        `/email-channels/${id}`,
        { isActive: !channel.isActive },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (!response.data) {
        throw new Error('Failed to toggle channel status');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleDefaultChannelStatus = createAsyncThunk(
  'emailChannels/toggleDefaultChannelStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await api.patch(
        `/email-channels/${id}`,
        { isDefault: true },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (!response.data) {
        throw new Error('Failed to set default channel');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const bulkDeleteChannels = createAsyncThunk(
  'emailChannels/bulkDeleteChannels',
  async (ids: string[], { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await api.post(
        `/email-channels/bulk-delete`,
        { ids },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (!response.data || response.data.status !== 'success') {
        throw new Error('Failed to delete channels');
      }
      return ids;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const bulkToggleStatus = createAsyncThunk(
  'emailChannels/bulkToggleStatus',
  async ({ ids, isActive }: { ids: string[]; isActive: boolean }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const response = await api.post(
        `/email-channels/bulk-toggle-status`,
        { ids, isActive },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      if (!response.data || response.data.status !== 'success') {
        throw new Error('Failed to update channels status');
      }
      return { ids, isActive };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const emailChannelsSlice = createSlice({
  name: 'emailChannels',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch channels
      .addCase(fetchChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChannels.fulfilled, (state, action) => {
        state.loading = false;
        state.channels = action.payload.channels;
        state.defaultChannel = action.payload.defaultChannel;
        state.hasDomainVerified = action.payload.hasDomainVerified;
      })
      .addCase(fetchChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create channel
      .addCase(createChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createChannel.fulfilled, (state, action) => {
        state.loading = false;
        state.channels.push(action.payload);
        if (action.payload.isDefault) {
          state.defaultChannel = action.payload;
        }
      })
      .addCase(createChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update channel
      .addCase(updateChannel.fulfilled, (state, action) => {
        const index = state.channels.findIndex(channel => channel.id === action.payload.id);
        if (index !== -1) {
          state.channels[index] = action.payload;
        }
        if (action.payload.isDefault) {
          state.defaultChannel = action.payload;
        }
      })
      // Delete channel
      .addCase(deleteChannel.fulfilled, (state, action) => {
        state.channels = state.channels.filter(channel => channel.id !== action.payload);
        if (state.defaultChannel && state.defaultChannel.id === action.payload) {
          state.defaultChannel = null;
        }
      })
      // Toggle channel status
      .addCase(toggleChannelStatus.fulfilled, (state, action) => {
        const index = state.channels.findIndex(channel => channel.id === action.payload.id);
        if (index !== -1) {
          state.channels[index] = action.payload;
        }
      })
      // Toggle default channel status
      .addCase(toggleDefaultChannelStatus.fulfilled, (state, action) => {
        state.channels = state.channels.map(channel => ({
          ...channel,
          isDefault: channel.id === action.payload.id
        }));
        state.defaultChannel = action.payload;
      })
      // Bulk delete channels
      .addCase(bulkDeleteChannels.fulfilled, (state, action) => {
        state.channels = state.channels.filter(channel => !action.payload.includes(channel.id));
        if (state.defaultChannel && action.payload.includes(state.defaultChannel.id)) {
          state.defaultChannel = null;
        }
      })
      // Bulk toggle status
      .addCase(bulkToggleStatus.fulfilled, (state, action) => {
        const { ids, isActive } = action.payload;
        state.channels = state.channels.map(channel =>
          ids.includes(channel.id) ? { ...channel, isActive } : channel
        );
      });
  },
});

// Export the reducer directly
export const emailChannelsReducer = emailChannelsSlice.reducer;
